<template>
  <div class="profile-comments">
    <div class="fixed-section sticky top-[61px] w-full bg-gray-50 z-20">
      <!-- Nav 選單 -->
      <div class="border-b border-gray-500">
        <a 
          v-for="link in commentLinks" 
          :key="link.key"
          :href="link.path"
          :class="getNavLinkClass(link.key)"
          class="border-b-4 tab inline-block"
        >
          <div class="flex items-center py-3.5 hover:text-gray-200">
            {{ $t(link.titleKey) }}
          </div>
        </a>
      </div>

      <!-- 搜尋 -->
      <div class="my-4">
        <search-input
          v-model="searchKeyword"
          :placeholder="$t('models.model_search_form.search_model')"
          @search="handleSearch"
        />
      </div>
 
      <!-- 列表表頭 -->      
      <div class="w-full text-12px flex flex-wrap gap-x-4 mt-4 sm:grid sm:gap-x-8 font-[600]
                sm:grid-cols-[72px_37fr_130fr_36px] 
                ">
        <div class="ml-2 text-gray-200">
          {{ $t('private_profiles.comment.thumbnail') }}
        </div>
        <div class=" text-gray-200 pl-4 sm:pl-0">
          {{ $t('private_profiles.comment.model_title') }}
        </div>
        <div class="hidden sm:block text-gray-200">
          {{ $t('private_profiles.comment.comment') }}
        </div>
      </div>

      <hr class='border-gray-500 my-2'>
    </div>
    <!-- 評論列表 -->
    <div v-if="stateData.items.length > 0" 
         class="comments-list flex-1 overflow-auto scrollbar pr-1 -mr-3"
    >
      <profile-review-item 
        v-for="(comment, index) in stateData.items" 
        :review="comment" 
        :is-last="index === stateData.items.length - 1"
      />

      <!-- 在有 items 且持續加載中，顯示 loading -->
      <template v-if="state.loading">
        <div class="flex flex-col justify-center items-center py-8">
          <div class="text-14px text-grey-3">
            <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
          </div>
        </div>
      </template>
      <!-- 在有 items 且加載完全後，顯示 No more data -->
      <template v-else-if="stateData.items.length > 0 && !state.loading && !pageInfo.hasMore">
        <div class="flex flex-col justify-center items-center py-8">
          <div class="text-14px text-grey-3">
            {{ $t('home_post_collection.no_more_data')  }}
          </div>
        </div>
      </template>
    </div>

    <div v-else-if="state.loading || state.initialLoading" class="flex-1 flex flex-col items-center justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
      <p class="text-16px font-medium text-center">
        <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </p>
    </div>

    <!-- 空狀態 -->
    <div v-else-if="stateData.items.length === 0 && !state.loading"
         class="flex-1 flex flex-col items-center justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]"
    >
      <img src="../../images/comments.svg" class="block w-8" alt="No comments" />
      <p class="text-16px font-medium text-center">
        {{ $t('private_profiles.comment.no_comment_fount') }}
      </p>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, onUnmounted, watch } from 'vue';
import { debounce } from 'lodash';

export default defineComponent({
  name: 'ProfileComments',
  setup() {
    const stateData = reactive({
      items: [],
      totalCount: 0
    });

    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 20,
    });

    const pageInfo = reactive({
      page: 1,
      hasMore: true,
      totalPages: 1
    });

    const searchKeyword = ref('');
    const selectedRating = ref('all');
    const scrollListener = ref(null);

    const currentUser = ref(null);

    const commentLinks = [
        {
          key: 'comments',
          titleKey: 'private_profiles.comment.comments',
          path: '/profile/comments'
        },
        {
          key: 'reviews',
          titleKey: 'private_profiles.comment.reviews_&_ratings',
          path: '/profile/reviews'
        }
      ]

    const fetchCurrentUser = async () => {
      try {
        const response = await fetch('/api/v1/profile/current_user');
        if (response.ok) {
          const data = await response.json();
          currentUser.value = data;
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    const filterOwnComments = (comments) => {
      if (!currentUser.value) return comments;
      
      return comments.filter(comment => {
        return comment.profile.id !== currentUser.value.id;
      });
    };

    const getNavLinkClass = (tabKey) => {
      return tabKey === 'comments' 
        ? 'border-primary font-medium flex-1 text-center px-4'
        : 'border-transparent flex-1 text-center px-4';
    };

    const resetPagination = () => {
      stateData.items = [];
      pageInfo.page = 1;
      pageInfo.hasMore = true;
      pageInfo.totalPages = 1;
    };

    const fetchComments = async (page, perPage) => {
      if (!pageInfo.hasMore || state.loading) return;

      state.loading = true;
      
      const url = `/api/v1/profile/comments`;
      
      const params = new URLSearchParams({
        page: page,
        per_page: perPage,
        kw: searchKeyword.value,
        type: 'comments'
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();
          
          const filteredItems = filterOwnComments(data.data);
          stateData.totalCount = data.pagination.total_count;

          filteredItems.forEach(item => {
            if (!stateData.items.some(existingItem => existingItem.id === item.id)) {
              stateData.items.push(item);
            }
          });

          
          pageInfo.totalPages = data.pagination.total_pages;
          pageInfo.hasMore = pageInfo.page < data.pagination.total_pages;
          pageInfo.page++;
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
        pageInfo.hasMore = false;
      } finally {
        state.loading = false;
        state.initialLoading = false;
      }
    };

    const handleSearch = async () => {
      resetPagination();
      state.initialLoading = true;
      await fetchComments(1, state.itemsCount);
    };

    const handleClear = async () => {
      searchKeyword.value = '';
      await handleSearch();
    };

    const handleScroll = debounce(() => {
      if (!pageInfo.hasMore) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrolledPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      
      if (scrolledPercentage > 70 && !state.loading) {
        fetchComments(pageInfo.page, state.itemsCount);
      }
    }, 100);

    const addScrollListener = () => {
      removeScrollListener();
      scrollListener.value = handleScroll;
      window.addEventListener('scroll', scrollListener.value, { passive: true });
    };

    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value);
        scrollListener.value = null;
      }
    };

    // 生命周期
    onMounted(async () => {
      await fetchCurrentUser();
      await fetchComments(1, state.itemsCount);
      addScrollListener();
    });

    onUnmounted(() => {
      removeScrollListener();
    });

    return {
      stateData,
      state,
      pageInfo,
      searchKeyword,
      selectedRating,
      commentLinks,
      getNavLinkClass,
      handleSearch,
      handleClear,
    };
  }
});
</script>